import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface ForecastData {
  date: string;
  predicted_kwh: number;
  confidence_interval: [number, number];
  weather_factor: number;
}

interface EnergyForecastChartProps {
  data: ForecastData[];
  loading?: boolean;
}

const EnergyForecastChart: React.FC<EnergyForecastChartProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="animate-pulse bg-gray-200 dark:bg-gray-700 w-full h-full rounded-lg"></div>
      </div>
    );
  }

  const chartData = useMemo(() => 
    data.map(item => ({
      date: new Date(item.date).toLocaleDateString(),
      predicted: Math.round(item.predicted_kwh),
      confidence: Math.round(item.weather_factor * 100)
    })), [data]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg">
          <p className="text-gray-900 dark:text-gray-100 font-medium mb-2">{label}</p>
          <div className="space-y-1">
            <p className="text-blue-600 dark:text-blue-400 font-semibold text-sm">
              Predicted: {data.predicted.toLocaleString()} kWh
            </p>
            <p className="text-green-600 dark:text-green-400 text-sm">
              Confidence: {data.confidence}%
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-64 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="2 2" className="opacity-30" stroke="currentColor" />
          <XAxis 
            dataKey="date" 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: 'currentColor' }}
            className="text-gray-600 dark:text-gray-400"
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: 'currentColor' }}
            className="text-gray-600 dark:text-gray-400"
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="predicted"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={{ fill: '#3b82f6', strokeWidth: 1, r: 3 }}
            animationDuration={400}
            name="Predicted Energy"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default EnergyForecastChart;